import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AxiosError } from "axios";
import { useAuth } from "../auth/AuthContext";

export function SignupPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await signUp({ email, company: company || undefined, phone: phone || undefined, password });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 409) {
        setError("An account with that email already exists.");
      } else if (err instanceof AxiosError && err.response?.status === 429) {
        setError("Too many signups from your network. Please try again later.");
      } else if (err instanceof AxiosError && err.response?.status === 422) {
        setError((err.response?.data?.detail as string) || "Please check your details and try again.");
      } else {
        setError("Sign up failed. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        <Link to="/" className="block text-center text-2xl font-extrabold text-brand mb-1">
          Reliadata
        </Link>
        <p className="text-center text-sm text-gray-500 mb-6">Create your account — it only takes a minute.</p>

        {error && <div className="bg-red-50 text-red-700 text-sm rounded px-3 py-2 mb-4">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="su-company" className="block text-sm text-gray-600 mb-1">Company / Organization</label>
            <input
              id="su-company"
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              autoComplete="organization"
            />
          </div>
          <div>
            <label htmlFor="su-email" className="block text-sm text-gray-600 mb-1">Work email *</label>
            <input
              id="su-email"
              type="email"
              required
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>
          <div>
            <label htmlFor="su-phone" className="block text-sm text-gray-600 mb-1">Phone</label>
            <input
              id="su-phone"
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="su-password" className="block text-sm text-gray-600 mb-1">Password *</label>
              <input
                id="su-password"
                type="password"
                required
                className="w-full border border-gray-300 rounded px-3 py-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div>
              <label htmlFor="su-confirm" className="block text-sm text-gray-600 mb-1">Confirm *</label>
              <input
                id="su-confirm"
                type="password"
                required
                className="w-full border border-gray-300 rounded px-3 py-2"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={busy}
            className="w-full bg-brand hover:bg-brand-dark text-white rounded py-2.5 font-semibold disabled:opacity-50"
          >
            {busy ? "Creating account…" : "Create account"}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-5">
          Already have an account?{" "}
          <Link to="/login" className="text-brand hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
